// FloatingMenuTooltip.js
// Tooltip class for managing the floating menu lifecycle and positioning

import {createRoot} from "react-dom/client";
import React from "react";
import {FloatingMenuComponent} from "./FloatingMenuComponent";
import {getLinkRangeAtCursor} from "./floatingMenuUtils";
import {
    handleFindExpert,
    handleLinkAction,
    handleLinkEntity,
    handleMetaMarkAction,
    handleMoreExamples
} from "./floatingMenuActions";
import {NodeSelection} from "prosemirror-state";
import {CellSelection} from "prosemirror-tables";

/**
 * Floating Menu Tooltip Class
 * Follows ProseMirror's tooltip pattern for proper positioning and lifecycle management
 */
export class FloatingMenuTooltip {
    constructor(view) {
        this.view = view;
        this.tooltip = this.createTooltip();
        this.shouldShow = false; // Track whether to show menu (set to true on mouseup / keyup)
        this.menuType = "selection";
        this.linkContext = null;
        this.selectedNodeInfo = null;
        this.pendingInlineChipClick = false;
        this.loadingButton = null; // Track which button is loading
        view.dom.parentNode.appendChild(this.tooltip);

        // Listen for mouseup on the editor to show menu after mouse selection
        this.handleMouseUp = this.onMouseUp.bind(this);
        view.dom.addEventListener("mouseup", this.handleMouseUp);

        // Escape: deselect and hide menu. Tab: trap focus inside floating menu buttons.
        this.handleKeyDown = this.onKeyDown.bind(this);
        view.dom.addEventListener("keydown", this.handleKeyDown);

        // When focus is inside the tooltip, Escape returns focus to the editor.
        this.handleTooltipKeyDown = this.onTooltipKeyDown.bind(this);
        this.tooltip.addEventListener("keydown", this.handleTooltipKeyDown);

        this.update(view, null);
    }

    onMouseUp(event) {
        const chipInfo = this.getSelectedInlineChipInfo(this.view.state);
        const target = event?.target;
        const clickedInlineChip = target
            ? Boolean(target.closest('.user-mention, .hashtag, .user-mention-complete, .hashtag-complete'))
            : false;

        if (clickedInlineChip) {
            this.pendingInlineChipClick = true;
        }

        // For chip clicks/selections, let update() resolve to nodeInfo mode once selection settles.
        if (chipInfo || clickedInlineChip) {
            this.shouldShow = Boolean(chipInfo);
            this.update(this.view, null);
            return;
        }

        // Allow menu to show on next update if there's a selection (or caret in link)
        this.shouldShow = true;
        this.update(this.view, null);
    }

    onKeyDown(event) {
        if (event.key === "Enter" && (event.ctrlKey || event.metaKey) && this.isAgenticSectionSelection(this.view.state)) {
            if (event.isComposing) return;
            const focused = this.focusSelectedAgenticSectionInput();
            if (focused) {
                event.preventDefault();
                event.stopPropagation();
            }
            return;
        }

        if (event.key === "Escape") {
            if (this.tooltip.style.display !== "none") {
                event.preventDefault();
                // Collapse selection to hide the menu
                const {state, dispatch} = this.view;
                dispatch(state.tr.setSelection(
                    state.selection.constructor.near(state.selection.$to)
                ));
                this.hideTooltipAndRefocus();
            }
            return;
        }

        if (event.key === "Tab" && this.tooltip.style.display !== "none") {
            const buttons = Array.from(this.tooltip.querySelectorAll("button:not([disabled])"));
            if (buttons.length === 0) return;
            event.preventDefault();
            // Move focus into the first button (Shift+Tab goes to the last)
            const target = event.shiftKey ? buttons[buttons.length - 1] : buttons[0];
            target.focus();
        }
    }

    isAgenticSectionSelection(state) {
        return state.selection instanceof NodeSelection
            && state.selection.node?.type?.name === "agenticSection";
    }

    focusSelectedAgenticSectionInput() {
        if (!this.isAgenticSectionSelection(this.view.state)) return false;

        const pos = this.view.state.selection.from;
        const nodeDom = this.view.nodeDOM(pos);
        if (!(nodeDom instanceof HTMLElement)) return false;

        const nodeView = nodeDom.pmViewDesc?.spec;
        if (nodeView && typeof nodeView.focusInput === "function") {
            return nodeView.focusInput();
        }

        const input = nodeDom.querySelector("textarea, input");
        if (!(input instanceof HTMLElement) || typeof input.focus !== "function") return false;
        input.focus();
        return true;
    }

    onTooltipKeyDown(event) {
        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            this.view.focus();
            return;
        }

        if (event.key === "Tab") {
            const buttons = Array.from(this.tooltip.querySelectorAll("button:not([disabled])"));
            if (buttons.length === 0) return;
            const current = document.activeElement;
            const idx = buttons.indexOf(current);
            if (event.shiftKey) {
                if (idx <= 0) {
                    // Wrap back to last button
                    event.preventDefault();
                    buttons[buttons.length - 1].focus();
                }
                // else let default Tab move to the previous button naturally
            } else {
                if (idx === buttons.length - 1 || idx === -1) {
                    // Wrap to first button
                    event.preventDefault();
                    buttons[0].focus();
                }
                // else let default Tab move to the next button naturally
            }
        }
    }

    createTooltip() {
        const tooltip = document.createElement("div");
        tooltip.className = "pm-floating-menu";
        tooltip.style.cssText = `
            position: absolute;
            display: none;
            z-index: 1000;
            pointer-events: auto;
            white-space: nowrap;
            width: max-content;
            max-width: none;
        `;

        // Create React root and render MUI component
        this.root = createRoot(tooltip);
        this.renderReact();

        return tooltip;
    }

    renderReact() {
        if (this._destroyed) return;
        this.root.render(
            <FloatingMenuComponent
                menuType={this.menuType}
                selectedNodeInfo={this.selectedNodeInfo}
                loadingButton={this.loadingButton}
                onNodeInfoClose={this.hideTooltipAndRefocus.bind(this)}
                onButtonClick={this.handleButtonClick.bind(this)}
            />
        );
    }

    setLoadingState(buttonType) {
        this.loadingButton = buttonType;
        this.renderReact();
    }

    isInlineChipNode(node) {
        const nodeType = node?.type?.name;
        return nodeType === "userMention" || nodeType === "hashtag";
    }

    buildSelectedNodeInfo(node) {
        if (!this.isInlineChipNode(node)) return null;

        if (node.type.name === "userMention") {
            return {
                type: "userMention",
                id: node.attrs.id || null,
                uid: node.attrs.id || null,
                label: node.attrs.label || null,
                name: node.attrs.name || null,
                email: node.attrs.email || null,
                position: node.attrs.position || null,
                headshot: node.attrs.headshot || null,
            };
        }

        return {
            type: "hashtag",
            id: node.attrs.id || null,
            label: node.attrs.label || null,
            docId: node.attrs.docId || null,
        };
    }

    getSelectedInlineChipInfo(state) {
        const {selection} = state;

        if (selection instanceof NodeSelection) {
            return this.buildSelectedNodeInfo(selection.node);
        }

        if (selection.empty) return null;

        const {$from, $to} = selection;
        if (!$from.sameParent($to)) return null;

        const fragment = $from.parent.content.cut($from.parentOffset, $to.parentOffset);
        if (fragment.childCount !== 1) return null;

        return this.buildSelectedNodeInfo(fragment.firstChild);
    }

    async handleButtonClick(actionType) {
        const {state} = this.view;

        // Link-context actions (caret inside a link)
        if (this.menuType === "link") {
            handleLinkAction(this.view, actionType, this.linkContext);
            this.hideTooltipAndRefocus();
            return;
        }

        // Selection actions
        const {from, to, empty} = state.selection;

        if (empty || from === to) return;

        // Handle different action types
        switch (actionType) {
            case "linkEntity":
                handleLinkEntity(this.view);
                this.hideTooltipAndRefocus();
                break;

            case "findExpert":
                await handleFindExpert(
                    this.view,
                    from,
                    to,
                    this.setLoadingState.bind(this),
                    this.hideTooltipAndRefocus.bind(this)
                );
                break;

            case "moreExamples":
                handleMoreExamples(this.view, from, to);
                this.hideTooltipAndRefocus();
                break;

            default:
                // Handle meta marks (comment, reference, etc.)
                handleMetaMarkAction(this.view, actionType);
                this.hideTooltipAndRefocus();
                break;
        }
    }

    hideTooltipAndRefocus() {
        this.tooltip.style.display = "none";
        this.shouldShow = false;
        this.menuType = "selection";
        this.linkContext = null;
        this.selectedNodeInfo = null;
        this.pendingInlineChipClick = false;
        this.loadingButton = null;
        this.renderReact();
        this.view.focus();
    }

    update(view, lastState) {
        const {state} = view;

        // Skip update if nothing changed
        if (lastState?.doc.eq(state.doc) && lastState?.selection.eq(state.selection)) return;

        // Suppress the generic floating menu only when a CellSelection is active
        // (table cells selected across rows/columns) — the tableFloatingMenuPlugin handles those.
        // Plain cursor or text selection *within* a cell still shows the generic menu.
        if (state.selection instanceof CellSelection) {
            this.tooltip.style.display = "none";
            this.shouldShow = false;
            this.selectedNodeInfo = null;
            return;
        }

        const linkCtx = getLinkRangeAtCursor(state);
        const selectedNodeInfo = this.getSelectedInlineChipInfo(state);

        if (this.isAgenticSectionSelection(state)) {
            this.tooltip.style.display = "none";
            this.shouldShow = false;
            this.selectedNodeInfo = null;
            return;
        }

        // Suppress the generic floating menu when a figure node is selected —
        // the figure's own image toolbar handles that state.
        if (
            state.selection instanceof NodeSelection &&
            state.selection.node?.type?.name === 'figure'
        ) {
            this.tooltip.style.display = "none";
            this.shouldShow = false;
            this.selectedNodeInfo = null;
            return;
        }

        // Suppress the generic floating menu when a map node is selected —
        // the map's own edit button handles user interaction.
        if (
            state.selection instanceof NodeSelection &&
            state.selection.node?.type?.name === 'map'
        ) {
            this.tooltip.style.display = "none";
            this.shouldShow = false;
            this.selectedNodeInfo = null;
            return;
        }

        if (!selectedNodeInfo && this.pendingInlineChipClick && !linkCtx) {
            this.tooltip.style.display = "none";
            this.shouldShow = false;
            return;
        }

        if (selectedNodeInfo) {
            this.pendingInlineChipClick = false;
        }

        // Hide if no selection and not in a link
        if (state.selection.empty && !linkCtx && !selectedNodeInfo) {
            this.tooltip.style.display = "none";
            this.shouldShow = false; // Reset flag when selection is cleared
            this.selectedNodeInfo = null;
            return;
        }

        // Auto-show for any non-empty selection (keyboard, mouse, programmatic, etc.)
        // mouseup also sets this, but doing it here covers all other input methods.
        if (!state.selection.empty) {
            this.shouldShow = true;
        }

        // Decide which menu to show
        const nextMenuType = linkCtx
            ? "link"
            : selectedNodeInfo
                ? "nodeInfo"
                : "selection";
        if (nextMenuType !== this.menuType) {
            this.menuType = nextMenuType;
            this.linkContext = linkCtx;
            this.selectedNodeInfo = selectedNodeInfo;
            this.renderReact();
        } else {
            this.linkContext = linkCtx;
            const nextInfo = JSON.stringify(selectedNodeInfo);
            const prevInfo = JSON.stringify(this.selectedNodeInfo);
            this.selectedNodeInfo = selectedNodeInfo;
            if (nextInfo !== prevInfo) {
                this.renderReact();
            }
        }

        // If selection menu but selection is empty, hide
        if (this.menuType === "selection" && state.selection.empty) {
            this.tooltip.style.display = "none";
            return;
        }

        this.tooltip.style.display = "";
        this.positionTooltip(view, state.selection, linkCtx);
    }

    positionTooltip(view, selection, linkCtx) {
        let start;
        let end;

        if (linkCtx) {
            // Anchor to caret when in link (selection is empty)
            start = view.coordsAtPos(selection.from);
            end = start;
        } else {
            const {from, to} = selection;
            start = view.coordsAtPos(from);
            end = view.coordsAtPos(to);
        }

        const box = this.tooltip.offsetParent.getBoundingClientRect();

        // Measure after display so the tooltip has a real size.
        const {width: tooltipWidth, height: tooltipHeight} = this.tooltip.getBoundingClientRect();

        const padding = 8;

        // ── Horizontal positioning ──────────────────────────────────────────
        // Default: center horizontally on selection midpoint.
        const selectionCenter = (start.left + end.left) / 2;

        // The left edge of the tooltip (relative to box) when centered.
        let leftRelative = selectionCenter - box.left - tooltipWidth / 2;

        // Clamp so the tooltip stays inside the editor with padding.
        const minLeft = padding;
        const maxLeft = box.width - tooltipWidth - padding;
        leftRelative = Math.max(minLeft, Math.min(leftRelative, maxLeft));

        this.tooltip.style.left = `${leftRelative}px`;
        this.tooltip.style.transform = "none";

        // ── Vertical positioning ────────────────────────────────────────────
        // Prefer above the selection; fall back to below if there's no room.
        const gapAbove = 8; // gap between menu and selection
        const topIfAbove = start.top - box.top - tooltipHeight - gapAbove;
        const topIfBelow = end.bottom - box.top + gapAbove;

        let topRelative;
        if (topIfAbove >= padding) {
            // Enough room above
            topRelative = topIfAbove;
        } else {
            // Not enough room above — position below, but clamp within editor
            topRelative = topIfBelow;
        }

        // Clamp vertically so the tooltip never overflows the editor container.
        const maxTop = box.height - tooltipHeight - padding;
        topRelative = Math.max(padding, Math.min(topRelative, maxTop));

        this.tooltip.style.top = `${topRelative}px`;
        this.tooltip.style.bottom = "";
    }

    destroy() {
        this.view.dom.removeEventListener("mouseup", this.handleMouseUp);
        this.view.dom.removeEventListener("keydown", this.handleKeyDown);
        this.tooltip.removeEventListener("keydown", this.handleTooltipKeyDown);
        this._destroyed = true;
        if (this.root) {
            // React 18 concurrent mode forbids synchronous unmount while a render is in
            // progress (e.g. when ProseMirror tears down the view inside a React effect
            // cleanup). Defer unmount to the next microtask so it always fires after the
            // current render cycle completes.
            const root = this.root;
            const tooltip = this.tooltip;
            queueMicrotask(() => {
                root.unmount();
                tooltip.remove();
            });
        } else {
            this.tooltip.remove();
        }
    }
}
